import { Dropdown, Button } from 'antd';
import classNames from 'classnames';
import React, { memo, ReactNode, useMemo, useState } from 'react';
import { ArrowDownIcon, CrossIcon } from '@icons';

export const SHOW_ALL_KEY = '__SHOW_ALL__';

type MultiSelectFilterProps = {
  onChange: (value: string[]) => void;
  items: {
    key: string;
    name: string;
    label: Element | ReactNode | string;
    className?: string;
  }[];
  selectedKeys: string[];
  placeholder?: string;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
};

export default memo(function MultiSelectFilter({
  onChange,
  items,
  selectedKeys,
  placeholder,
  hasMore,
  isLoadingMore,
  onLoadMore
}: MultiSelectFilterProps) {
  const [open, setOpen] = useState(false);

  const dropdownTitle = useMemo(() => {
    if (!selectedKeys.length) return '';
    return items
      .filter((item) => item && selectedKeys.includes(item.key))
      .map((status) => status.name)
      .join(', ');
  }, [selectedKeys, items]);

  return (
    <Dropdown
      trigger={['click']}
      placement="bottomLeft"
      open={open}
      onOpenChange={setOpen}
      menu={{
        multiple: true,
        selectable: true,
        items,
        style: {
          padding: '16px 8px',
          width: 'min-content'
        },
        selectedKeys: selectedKeys.length ? selectedKeys : [SHOW_ALL_KEY],
        onScroll: (e) => {
          if (!hasMore) return;
          if (isLoadingMore) return;
          if (typeof onLoadMore !== 'function') return;
          const target = e.target as HTMLDivElement;
          const isScrollBottom =
            target.scrollTop + target.clientHeight > target.scrollHeight - 10;
          if (hasMore && isScrollBottom) onLoadMore();
        },
        onSelect: ({ key }) => {
          const status = key as string;
          onChange(key === SHOW_ALL_KEY ? [] : [...selectedKeys, status]);
          setOpen(false);
        },
        onDeselect: ({ key }) => {
          onChange(selectedKeys.filter((x) => x !== key));
          setOpen(false);
        }
      }}
    >
      <div className="filter-item" style={{ width: '100%', maxWidth: 150 }}>
        {selectedKeys.length > 0 && (
          <Button
            type="text"
            className="filter-item__clear"
            onClick={() => onChange([])}
          >
            <CrossIcon />
          </Button>
        )}
        <span
          className={classNames(
            'filter-item__name',
            !!selectedKeys.length && 'filter-item__active-name'
          )}
        >
          {selectedKeys.length ? dropdownTitle : placeholder || 'Select'}
        </span>
        <div className="filter-item-icon">
          <ArrowDownIcon />
        </div>
      </div>
    </Dropdown>
  );
});
